
import { useState, useEffect } from 'react';
import { supabase } from '../../../lib/supabase';

interface Player {
  id: string;
  name: string;
  number: number;
  position: string;
  photo?: string;
  photo_url?: string;
  image_url?: string;
  team_id: string;
}

interface Staff {
  id: string;
  name: string;
  role: string;
  photo?: string;
  photo_url?: string;
  image_url?: string;
  team_id: string;
}

interface Team {
  id: string;
  name: string;
  category: string;
  logo?: string;
  photo?: string;
  imagen_equipo?: string;
  main_photo_url?: string;
  players: Player[];
  staff: Staff[];
}

export default function TeamsSection() {
  const [selectedTeam, setSelectedTeam] = useState<string>('');
  const [teams, setTeams] = useState<Team[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadTeams();
  }, []);

  const loadTeams = async () => {
    try {
      setLoading(true);
      
      // Solo cargar equipos del club (tabla teams, no opponent_teams)
      const [teamsResponse, playersResponse, staffResponse] = await Promise.all([
        supabase.from('teams').select('*'),
        supabase.from('players').select('*'),
        supabase.from('staff').select('*')
      ]);

      if (teamsResponse.error) throw teamsResponse.error;
      if (playersResponse.error) throw playersResponse.error;
      if (staffResponse.error) throw staffResponse.error;

      // Solo equipos del club
      const clubTeams = teamsResponse.data || [];

      // Organizar datos por equipo
      const teamsWithData = clubTeams.map(team => ({
        ...team,
        players: (playersResponse.data || []).filter(player => player.team_id === team.id),
        staff: (staffResponse.data || []).filter(staff => staff.team_id === team.id)
      }));

      // Ordenar equipos por categoría (de mayor a menor)
      const teamOrder = ['VIUBÀSQUET A', 'VIUBÀSQUET B', 'MINI', 'PREMINI'];
      const sortedTeams = teamsWithData.sort((a, b) => {
        const indexA = teamOrder.indexOf(a.name.toUpperCase());
        const indexB = teamOrder.indexOf(b.name.toUpperCase());
        
        // Si ambos están en el orden, usar el índice
        if (indexA !== -1 && indexB !== -1) {
          return indexA - indexB;
        }
        // Si solo uno está en el orden, ese va primero
        if (indexA !== -1) return -1;
        if (indexB !== -1) return 1;
        // Si ninguno está en el orden, mantener orden alfabético
        return a.name.localeCompare(b.name);
      });

      setTeams(sortedTeams);
      
      // Seleccionar el primer equipo por defecto
      if (sortedTeams.length > 0 && !selectedTeam) {
        setSelectedTeam(sortedTeams[0].id);
      }
    } catch (error) {
      console.error('Error loading teams:', error);
    } finally {
      setLoading(false);
    }
  };

  const currentTeam = teams.find(team => team.id === selectedTeam);

  const getPositionColor = (position: string) => {
    switch (position) {
      case 'Base': return 'bg-blue-100 text-blue-800';
      case 'Escolta': return 'bg-green-100 text-green-800';
      case 'Alero': return 'bg-yellow-100 text-yellow-800';
      case 'Ala pívot': return 'bg-purple-100 text-purple-800';
      case 'Pívot': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getPositionOrder = (position: string) => {
    switch (position) {
      case 'Base': return 1;
      case 'Escolta': return 2;
      case 'Alero': return 3;
      case 'Ala pívot': return 4;
      case 'Pívot': return 5;
      default: return 6;
    }
  };

  if (loading) {
    return (
      <section className="px-4 py-8 bg-gray-900">
        <div className="max-w-md mx-auto">
          <div className="text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-pink-500 mx-auto"></div>
            <p className="mt-2 text-gray-300">Cargando equipos...</p>
          </div>
        </div>
      </section>
    );
  }

  if (teams.length === 0) {
    return (
      <section className="px-4 py-8 bg-gray-900">
        <div className="max-w-md mx-auto">
          <div className="bg-gray-800 rounded-xl p-6 shadow-sm text-center">
            <p className="text-gray-400">No hay equipos registrados aún.</p>
            <p className="text-gray-500 text-sm mt-2">Los equipos añadidos desde el panel de administración aparecerán aquí.</p>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="px-4 py-8 bg-gray-900">
      <div className="max-w-md mx-auto">
        {/* Team Selector */}
        <div className="bg-gray-800 rounded-xl p-1 mb-6 shadow-sm">
          <div className="grid grid-cols-2 gap-1">
            {teams.map((team) => (
              <button
                key={team.id}
                onClick={() => setSelectedTeam(team.id)}
                className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                  selectedTeam === team.id
                    ? 'bg-pink-500 text-white'
                    : 'text-gray-300 hover:text-white'
                }`}
              >
                {team.name}
              </button>
            ))}
          </div>
        </div>

        {/* Team Photo and Info */}
        {currentTeam && (
          <div className="bg-gray-800 rounded-xl shadow-sm overflow-hidden mb-6">
            <div className="relative">
              <img
                src={currentTeam.imagen_equipo || currentTeam.main_photo_url || currentTeam.photo || 'https://readdy.ai/api/search-image?query=Professional%20basketball%20team%20photo%2C%20group%20of%20basketball%20players%20in%20pink%20and%20white%20uniforms%2C%20team%20portrait%2C%20sports%20photography%2C%20high%20quality%2C%20well-lit%20indoor%20basketball%20court%20background%2C%20players%20standing%20together%20in%20formation%2C%20confident%20poses%2C%20athletic%20build%2C%20basketball%20equipment%20visible&width=400&height=300&seq=default-team&orientation=landscape'}
                alt={`Equipo ${currentTeam.name}`}
                className="w-full h-48 object-cover object-top"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent"></div>
              <div className="absolute bottom-4 left-4 text-white">
                <h3 className="text-xl font-bold">{currentTeam.name}</h3>
                <p className="text-pink-200 font-medium">{currentTeam.category}</p>
              </div>
            </div>
          </div>
        )}

        {/* Staff Section */}
        {currentTeam && (
          <div className="bg-gray-800 rounded-xl shadow-sm overflow-hidden mb-6">
            <div className="bg-gray-700 text-white p-4">
              <h3 className="font-semibold text-center">Cuerpo Técnico</h3>
            </div>
            
            <div className="p-4">
              {currentTeam.staff.length === 0 ? (
                <p className="text-center text-gray-400 py-8">No hay cuerpo técnico registrado</p>
              ) : (
                <div className="grid gap-3">
                  {currentTeam.staff.map((member) => (
                    <div key={member.id} className="flex items-center gap-4 p-3 bg-gray-700 rounded-lg">
                      <img
                        src={member.photo_url || member.photo || member.image_url || 'https://media.istockphoto.com/id/846183008/es/vector/%C3%ADcono-de-perfil-de-avatar-por-defecto-marcador-de-posici%C3%B3n-de-foto-gris.jpg?s=612x612&w=0&k=20&c=CLZoOwpSgoDpY_4ELU9OaY23p0B0mwjXCfbiyc7g9u4='}
                        alt={member.name}
                        className="w-12 h-12 rounded-full object-cover"
                      />
                      
                      <div className="flex-1">
                        <h4 className="font-medium text-white">{member.name}</h4>
                        <p className="text-gray-300 text-sm">{member.role}</p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Players List */}
        {currentTeam && (
          <div className="bg-gray-800 rounded-xl shadow-sm overflow-hidden">
            <div className="bg-pink-500 text-white p-4">
              <h3 className="font-semibold text-center">Plantilla</h3>
            </div>
            
            <div className="p-4">
              {currentTeam.players.length === 0 ? (
                <p className="text-center text-gray-400 py-8">No hay jugadores registrados</p>
              ) : (
                <div className="grid gap-3">
                  {currentTeam.players
                    .sort((a, b) => getPositionOrder(a.position) - getPositionOrder(b.position))
                    .map((player) => (
                    <div key={player.id} className="flex items-center gap-4 p-3 bg-gray-700 rounded-lg">
                      <img
                        src={player.photo_url || player.photo || player.image_url || 'https://media.istockphoto.com/id/846183008/es/vector/%C3%ADcono-de-perfil-de-avatar-por-defecto-marcador-de-posici%C3%B3n-de-foto-gris.jpg?s=612x612&w=0&k=20&c=CLZoOwpSgoDpY_4ELU9OaY23p0B0mwjXCfbiyc7g9u4='}
                        alt={player.name}
                        className="w-12 h-12 rounded-full object-cover"
                      />
                      
                      <div className="flex-1">
                        <h4 className="font-medium text-white">{player.name}</h4>
                        <div className="flex items-center gap-2 mt-1">
                          <span className={`px-2 py-1 rounded-full text-xs font-medium ${getPositionColor(player.position)}`}>
                            {player.position}
                          </span>
                        </div>
                      </div>

                      <div className="text-right">
                        <span className="text-3xl font-bold text-pink-500">#{player.number}</span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Team Stats Summary */}
        {currentTeam && (
          <div className="mt-6 bg-gray-800 rounded-xl p-6 shadow-sm">
            <h4 className="font-semibold text-white mb-4">Información del Equipo</h4>
            <div className="grid grid-cols-2 gap-4">
              <div className="text-center">
                <div className="text-2xl font-bold text-pink-500">
                  {currentTeam.players.length}
                </div>
                <div className="text-xs text-gray-400">Jugadores</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-gray-400">
                  {currentTeam.staff.length}
                </div>
                <div className="text-xs text-gray-400">Staff</div>
              </div>
            </div>
          </div>
        )}
      </div>
    </section>
  );
}